/**
 * CycleReportPage — Brief G Correction §2, §3
 *
 * Route: /prioritization/cycles/:cycleId/report
 *        /prioritization/cycles/:cycleId/report/:version
 *
 * Self-contained page (not a tab on CycleDetail) that renders the Cycle Report
 * with full provenance. Per correction §3:
 *
 *   - "Ratified by [name] on [date]"
 *   - "Based on [N] sessions across [N] loops"
 *   - "Value source: [collection name] v[N]" or "Workspace values"
 *   - "Proposal submitted by [name] · Accepted by [name] on [date]"
 *   - Version indicator with supersedes chain
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useCycleReportStore } from '@/stores/prioritization/cycleReportStore';
import { useCycleStore } from '@/stores/prioritization/degapProcessStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { CycleReportView } from '@/components/prioritization/CycleReportView';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '@/lib/supabaseSync';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import type { CycleReport } from '@/stores/prioritization/cycleReportStore';
import { RatificationNextSteps } from '@/components/prioritization/catchball/RatificationNextSteps';

// ─── Provenance bar ───────────────────────────────────────────────────────────

interface ProposalMeta {
  proposedByName: string | null;
  proposedAt: string | null;
  acceptedById: string | null;
  acceptedAt: string | null;
}

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function ProvenanceBar({ report }: { report: CycleReport }) {
  const [proposalMeta, setProposalMeta] = useState<ProposalMeta | null>(null);

  useEffect(() => {
    if (!report.proposalId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    void (async () => {
      const { data } = await supabase
        .from('catchball_proposals')
        .select('proposed_by_name, proposed_at, resolved_by, resolved_at')
        .eq('id', report.proposalId!)
        .single();

      if (!data) return;
      const row = data as Record<string, unknown>;

      setProposalMeta({
        proposedByName: row.proposed_by_name as string | null,
        proposedAt: row.proposed_at as string | null,
        acceptedById: (row.resolved_by as string | null) ?? null,
        acceptedAt: row.resolved_at as string | null,
      });
    })();
  }, [report.proposalId]);

  // Resolve actor UUIDs to display names via the canonical hook (SECURITY
  // DEFINER RPC — works regardless of users-table RLS, unlike a direct read).
  const actorNames = useActorDisplayMap([report.ratifiedBy, proposalMeta?.acceptedById]);
  const ratifiedByName = report.ratifiedBy ? actorNames[report.ratifiedBy] ?? null : null;
  const acceptedByName = proposalMeta?.acceptedById
    ? actorNames[proposalMeta.acceptedById] ?? null
    : null;

  const por = report.planOfRecord;

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface-raised)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)] text-sm">
      {/* Ratification */}
      <ProvenanceRow
        label="Ratified"
        value={
          report.ratifiedAt
            ? `${fmtDate(report.ratifiedAt)}${ratifiedByName ? ` · by ${ratifiedByName}` : ''}`
            : '—'
        }
      />

      {/* Session / loop counts */}
      <ProvenanceRow
        label="Evidence base"
        value={`${por?.session_count ?? 0} session${(por?.session_count ?? 0) !== 1 ? 's' : ''} across ${por?.loop_count ?? 0} loop${(por?.loop_count ?? 0) !== 1 ? 's' : ''}`}
      />

      {/* Value source */}
      <ProvenanceRow
        label="Value source"
        value={por?.value_collection_snapshot_id
          ? `Snapshot ${por.value_collection_snapshot_id.slice(0, 8)}…`
          : 'Workspace values'}
      />

      {/* Proposal chain */}
      {proposalMeta && (
        <ProvenanceRow
          label="Proposal"
          value={[
            proposalMeta.proposedByName ? `Submitted by ${proposalMeta.proposedByName}${proposalMeta.proposedAt ? ` on ${fmtDate(proposalMeta.proposedAt)}` : ''}` : null,
            acceptedByName ? `Accepted by ${acceptedByName}${proposalMeta.acceptedAt ? ` on ${fmtDate(proposalMeta.acceptedAt)}` : ''}` : null,
          ].filter(Boolean).join(' · ')}
        />
      )}

      {/* Version chain */}
      <ProvenanceRow
        label="Version"
        value={report.version === 1
          ? 'v1 — first version of this Published Priority List'
          : `v${report.version}${report.supersedesId ? ` — supersedes v${report.version - 1}` : ''}${report.rationale ? ` · ${report.rationale}` : ''}`
        }
      />
    </div>
  );
}

function ProvenanceRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-4 px-4 py-2.5">
      <span className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wide w-28 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-pri)] leading-relaxed">{value}</span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CycleReportPage() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by the Executive Inbox when Accept just landed here (Doc 26 rule 6):
  // the executive arrives on the artifact their decision produced.
  const navState = location.state as
    | { ratifiedMoment?: boolean; loopId?: string | null }
    | null;
  const ratifiedMoment = navState?.ratifiedMoment === true;
  const userId = useUserStore(s => s.userId);

  const cycles = useCycleStore(s => s.cycles);
  const fetchFromSupabase = useCycleStore(s => s.fetchFromSupabase);
  const syncState = useCycleStore(s => s.syncState);
  const cycle = cycles.find(c => c.id === cycleId);

  const fetchCurrentCycleReport = useCycleReportStore(s => s.fetchCurrentCycleReport);
  const writeCycleReport = useCycleReportStore(s => s.writeCycleReport);

  const [report, setReport] = useState<CycleReport | null>(null);
  const [loading, setLoading] = useState(true);
  /** Accepted Catchball packet present but no cycle_reports row yet. */
  const [repairCtx, setRepairCtx] = useState<{ loopId: string; proposalId: string } | null>(null);
  /** Loop behind the accepted packet — drives the "what happens next" checklist (F4). */
  const [resolvedLoopId, setResolvedLoopId] = useState<string | null>(navState?.loopId ?? null);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    const r = await fetchCurrentCycleReport(cycleId);
    setReport(r);

    // The most recent accepted packet answers two questions at once: whether
    // this is a "ratified but report missing" repair case, and which loop the
    // remaining governance steps belong to.
    const supabase = getSafeSupabaseClient();
    let acceptedPacket: { id: string; loop_id: string } | null = null;
    if (supabase) {
      const { data } = await supabase
        .from('catchball_proposals')
        .select('id, loop_id, status')
        .eq('cycle_id', cycleId)
        .in('status', ['accepted', 'accepted_provisional'])
        .not('loop_id', 'is', null)
        .order('resolved_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data?.id && data.loop_id) {
        acceptedPacket = { id: data.id as string, loop_id: data.loop_id as string };
      }
    }

    setResolvedLoopId((prev) => prev ?? acceptedPacket?.loop_id ?? null);
    // Only claim "ratified but report missing" when an accepted packet exists.
    setRepairCtx(
      !r && acceptedPacket
        ? { proposalId: acceptedPacket.id, loopId: acceptedPacket.loop_id }
        : null,
    );
    setLoading(false);
  }, [cycleId, fetchCurrentCycleReport]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  // Fetch cycle if not in local state
  useEffect(() => {
    if (cycleId && !cycle && !syncState.isLoading) {
      void fetchFromSupabase();
    }
  }, [cycleId, cycle, syncState.isLoading, fetchFromSupabase]);

  const handleRetryReport = async () => {
    if (!cycleId || !userId || !repairCtx) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const reportId = await writeCycleReport({
        cycleId,
        loopId: repairCtx.loopId,
        proposalId: repairCtx.proposalId,
        actorId: userId,
        workspaceId: getCurrentWorkspaceId() ?? cycle?.workspaceId ?? '',
      });
      if (!reportId) {
        setRetryError('Could not generate the Published Priority List. Check that the proposal has a ranking and try again.');
        return;
      }
      await loadReport();
    } finally {
      setRetrying(false);
    }
  };

  const reportMissing = !report && !!repairCtx;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] gap-3 text-[var(--text-ter)]">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading cycle report…</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-16">

      {/* Back link */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(`/prioritization/cycles/${cycleId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {cycle?.name ?? 'Cycle'}
        </button>
        <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)]">/</span>
        <span className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">Published Priority List</span>
      </div>

      {/* Ratified moment — Accept landed here, confirm what it produced */}
      {ratifiedMoment && report && (
        <div className="border border-[var(--green)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20 dark:border-[var(--green)] p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-7 w-7 text-[var(--green)] dark:text-[var(--text-ter)] flex-shrink-0" />
            <div>
              <p className="text-lg font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                Priorities ratified
              </p>
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">
                Your acceptance created the cycle report — the ratified priorities for
                this cycle. The team that sent the proposal has been resolved. Next step:
                publish these priorities to your organization.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* What still stands between ratification and the Plan of Record (F4) */}
      {ratifiedMoment && resolvedLoopId && (
        <RatificationNextSteps cycleId={cycleId!} loopId={resolvedLoopId} />
      )}

      {/* Accepted packet exists but cycle_reports row missing */}
      {reportMissing && (
        <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 dark:border-[var(--mustard)] p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-[var(--mustard)] dark:text-[var(--mustard)] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-[var(--mustard)] dark:text-[var(--mustard)]">
                Proposal accepted — Published Priority List not generated yet
              </p>
              <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] mt-0.5">
                Provisional Accept does not write the cycle report by itself. Retry generates it from the accepted ranking, or use Accept &amp; ratify on the proposal.
              </p>
              {retryError && (
                <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)] mt-2">{retryError}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => void handleRetryReport()}
              disabled={retrying || !userId}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--mustard)] text-sm text-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
              {retrying ? 'Generating…' : 'Retry'}
            </button>
          </div>
        </div>
      )}

      {/* Provenance bar (only when report exists) */}
      {report && (
        <>
          <ProvenanceBar report={report} />
          <CycleReportView cycleId={cycleId!} />
        </>
      )}

      {/* Completely empty (no ratification yet) */}
      {!report && !reportMissing && (
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-12 text-center">
          <p className="font-medium text-[var(--text-sec)]">No Published Priority List yet</p>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            A Published Priority List is created automatically when an executive accepts a Catchball proposal.
          </p>
          <Link
            to={`/prioritization/cycles/${cycleId}`}
            className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            Return to cycle
          </Link>
        </div>
      )}
    </div>
  );
}

export default CycleReportPage;
